import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ShoppingCart, ChevronLeft, ChevronRight, User, X,
  Plus, Minus, ShoppingBag, MapPin, Star, Package, Home,
  ShoppingBasket, ArrowLeft, Check, ChevronDown, LayoutGrid,
} from "lucide-react";

import logo from "../../assets/app/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToBrandProductsFeed,
  stopBrandProductsFeed,
} from "../../redux/productSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { listenToCommerceData } from "../../redux/commercePartnersSlice";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";

import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import { Notifications } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../redux/notificationsSlice";

/* ═══════════════════════════════════════════════════════════
   CATEGORY + ATTRIBUTE SOURCES — every non-food vertical,
   same data this app uses on ProductsHome, so StoreHome can
   show real icons/names instead of raw categoryId/categoryName
   strings, and can offer the same sidebar/bottom-sheet browse
   experience scoped to just this brand's own catalogue.
═══════════════════════════════════════════════════════════ */
import { giftsCategories, giftsSubcategories, giftsProducts, renderGiftsIcon } from "../../CATEGORIES/GiftsCategories";
import {
  FashionCategories,
  FashionClothing,
  FashionAccessories,
  FashionSunglasses,
  FashionHats,
  FashionWatches,
  FashionJewellery,
  FashionNecklaces,
  FashionRings,
  FashionShoes,
  FashionBags,
} from "../../CATEGORIES/FashionCategories";
import {
  BabyMaternityCategories,
  BabyClothing,
  BabyShoes,
  BabyCare,
  MommyCare,
  BabyFeeding,
  BabyBedtime,
  BabyTravel,
  BabyToys,
  BabyNursery,
  Maternity,
  BabyHealthSafety,
  ActivityEntertainment,
  BabyAccessories,
  PottyTraining,
  Gifts as MaternityGifts,
  BabyStationery,
  CarSeatsAccessories,
  Clothing as MaternityClothing,
  BabyTeething,
  AllBabyMaternityProducts,
} from "../../CATEGORIES/BabyCategories";
import {
  BeautyCategories,
  BeautySkincareSubcategories,
  BeautyMakeupSubcategories,
  BeautyHaircareSubcategories,
  BeautyFragranceSubcategories,
  BeautyBodySubcategories,
  BeautyNailsSubcategories,
  BeautyToolsSubcategories,
  BeautyMensGroomingSubcategories,
  BeautyWellnessSubcategories,
  BeautySetsSubcategories,
  AllBeautyProducts,
} from "../../CATEGORIES/BeautyCategories";
import { electronicsCategories, electronicsSubcategories, electronicsProducts } from "../../CATEGORIES/ElectronicsCategories";
import { idToysCategories, idToysSubcategories, idToysProducts } from "../../CATEGORIES/ToysCategories";
import { idGamingCategories, idGamingSubcategories, idGamingProducts } from "../../CATEGORIES/GamingCategories";
import { sportsCategories, sportsSubcategories, sportsProducts } from "../../CATEGORIES/SportsCategories";
import { idArtCraftsCategories, idArtCraftsSubcategories, idArtCraftsProducts } from "../../CATEGORIES/ArtCraftsCategories";
import { idAviationCategories, idAviationSubcategories, idAviationProducts } from "../../CATEGORIES/AviationCategories";
import { automotiveCategories, automotiveSubcategories, automotiveProducts } from "../../CATEGORIES/AutomotiveCategories";
import { toolsCategories, toolsSubcategories, toolsProducts } from "../../CATEGORIES/ToolsCategories";
import { idPetsCategories, idPetsSubcategories, idPetsProducts } from "../../CATEGORIES/PetsCategories";
import { idFurnitureCategories, idFurnitureSubcategories, idFurnitureProducts } from "../../CATEGORIES/FurnitureCategories";
import { kitchenCategories, kitchenSubcategories, kitchenProducts } from "../../CATEGORIES/KitchenCategories";
import { idLuggageCategories, idLuggageSubcategories, idLuggageProducts } from "../../CATEGORIES/LuggageCategories";
import { idBooksCategories, idBooksSubcategories, idBooksProducts } from "../../CATEGORIES/BooksCategories";

import {
  Sizes,
  ShoeSizes,
  productColors,
  GENERAL_COLOR_ATTRIBUTE_ID,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — PEEKABEBI: logo orange→red + cream/peach
═══════════════════════════════════════════════════════════ */
const F = {
  pageBg:      "#FFF8F1",
  headerBg:    "#FF6A2A",
  cardBg:      "#FFFFFF",
  cream:       "#FFEDDA",
  grad:        "linear-gradient(135deg,#FF9A3D 0%,#FF3D57 100%)",
  gradH:       "linear-gradient(90deg,#FF9A3D 0%,#FF3D57 100%)",
  gradAnim:    "linear-gradient(90deg,#FFB347 0%,#FF3D57 50%,#FFB347 100%)",
  orange:      "#FF7A1A",
  red:         "#FF3D57",
  green:       "#2C5E34",
  white:       "#FFFFFF",
  offwhite:    "#FFF3E6",
  ink:         "#2B1A12",
  muted:       "#9C8A7E",
  mutedDim:    "#B79C87",
  border:      "rgba(43,26,18,0.10)",
  orangeOutline: "1.5px solid rgba(255,61,87,0.35)",
};

const GRAD_H = F.gradH;
const RGBA_DARK   = (a: number) => `rgba(43,26,18,${a})`;
const RGBA_ORANGE = (a: number) => `rgba(255,122,26,${a})`;
const RGBA_RED    = (a: number) => `rgba(255,61,87,${a})`;

/* ═══════════════════════════════════════════════════════════
   CATEGORY SOURCES — non-food only (fastfood / grocery / farm
   are intentionally excluded; this is a general-merchandise
   store shell, not a restaurant/grocery one)
═══════════════════════════════════════════════════════════ */
const FASHION_ADULT_CATEGORIES = FashionCategories.filter(c => c.id >= 11 && c.id <= 15);
const FASHION_CLOTHING_ADULT = FashionClothing.filter(c => ![1101, 1102, 1103].includes(c.id));
const FASHION_ADULT_SUBCATEGORIES = [
  ...FASHION_CLOTHING_ADULT,
  ...FashionAccessories,
  ...FashionJewellery,
  ...FashionShoes,
  ...FashionBags,
];
const FASHION_ADULT_PRODUCTS = [
  ...FashionSunglasses,
  ...FashionHats,
  ...FashionWatches,
  ...FashionNecklaces,
  ...FashionRings,
];
const BEAUTY_ADULT_CATEGORIES = BeautyCategories.filter(c => c.id !== 210);
const BEAUTY_ADULT_SUBCATEGORIES = [
  ...BeautySkincareSubcategories,
  ...BeautyMakeupSubcategories,
  ...BeautyHaircareSubcategories,
  ...BeautyFragranceSubcategories,
  ...BeautyBodySubcategories,
  ...BeautyNailsSubcategories,
  ...BeautyToolsSubcategories,
  ...BeautyMensGroomingSubcategories,
  ...BeautyWellnessSubcategories,
  ...BeautySetsSubcategories,
];
const MATERNITY_SUBCATEGORIES = [
  ...BabyClothing, ...BabyShoes, ...BabyCare, ...MommyCare, ...BabyFeeding,
  ...BabyBedtime, ...BabyTravel, ...BabyToys, ...BabyNursery, ...Maternity,
  ...BabyHealthSafety, ...ActivityEntertainment, ...BabyAccessories,
  ...PottyTraining, ...MaternityGifts, ...BabyStationery,
  ...CarSeatsAccessories, ...MaternityClothing, ...BabyTeething,
];

type CatSource = { slug: string; label: string; accent: string; roots: any[]; subs: any[]; products: any[] };

const CATEGORY_SOURCES: CatSource[] = [
  { slug: "gifts",       label: "Gifts",        accent: "#E91E63", roots: giftsCategories,           subs: giftsSubcategories,           products: giftsProducts },
  { slug: "fashion",     label: "Fashion",      accent: F.ink,     roots: FASHION_ADULT_CATEGORIES,  subs: FASHION_ADULT_SUBCATEGORIES,  products: FASHION_ADULT_PRODUCTS },
  { slug: "beauty",      label: "Beauty",       accent: "#C9A84C", roots: BEAUTY_ADULT_CATEGORIES,   subs: BEAUTY_ADULT_SUBCATEGORIES,   products: AllBeautyProducts },
  { slug: "maternity",   label: "Maternity",    accent: "#F48FB1", roots: BabyMaternityCategories,   subs: MATERNITY_SUBCATEGORIES,      products: AllBabyMaternityProducts },
  { slug: "furniture",   label: "Furniture",    accent: "#6D4C41", roots: idFurnitureCategories,     subs: idFurnitureSubcategories,     products: idFurnitureProducts },
  { slug: "electronics", label: "Electronics",  accent: "#1565C0", roots: electronicsCategories,     subs: electronicsSubcategories,     products: electronicsProducts },
  { slug: "toys",        label: "Toys",         accent: "#FFC107", roots: idToysCategories,          subs: idToysSubcategories,          products: idToysProducts },
  { slug: "gaming",      label: "Gaming",       accent: "#6A1B9A", roots: idGamingCategories,        subs: idGamingSubcategories,        products: idGamingProducts },
  { slug: "sports",      label: "Sports",       accent: "#388E3C", roots: sportsCategories,          subs: sportsSubcategories,          products: sportsProducts },
  { slug: "artcrafts",   label: "Art & Crafts", accent: "#8E44AD", roots: idArtCraftsCategories,     subs: idArtCraftsSubcategories,     products: idArtCraftsProducts },
  { slug: "aviation",    label: "Aviation",     accent: "#0277BD", roots: idAviationCategories,      subs: idAviationSubcategories,      products: idAviationProducts },
  { slug: "automotive",  label: "Automotive",   accent: "#455A64", roots: automotiveCategories,      subs: automotiveSubcategories,      products: automotiveProducts },
  { slug: "tools",       label: "Tools",        accent: "#607D8B", roots: toolsCategories,           subs: toolsSubcategories,           products: toolsProducts },
  { slug: "pets",        label: "Pets",         accent: "#8D6E63", roots: idPetsCategories,          subs: idPetsSubcategories,          products: idPetsProducts },
  { slug: "kitchen",     label: "Kitchen",      accent: "#D84315", roots: kitchenCategories,         subs: kitchenSubcategories,         products: kitchenProducts },
  { slug: "luggage",     label: "Luggage",      accent: "#37474F", roots: idLuggageCategories,       subs: idLuggageSubcategories,       products: idLuggageProducts },
  { slug: "books",       label: "Books",        accent: "#3F51B5", roots: idBooksCategories,         subs: idBooksSubcategories,         products: idBooksProducts },
];

/* ═══════════════════════════════════════════════════════════
   FLAT CATEGORY MAP — every root/sub/product across every
   vertical this store shell carries, keyed by id. This lets
   StoreHome resolve ANY id a listing is tagged with (categoryId,
   subcategoryId, OR productId) back up to its top-level root at
   ANY depth — instead of assuming a fixed 2-hop shape. Mirrors
   the approach CategoryDetails.tsx uses, which is why that page
   never misses a category detection this one used to.
═══════════════════════════════════════════════════════════ */
type CatEntry = {
  id: number; name: string; icon?: any; image?: any; color?: string;
  vertical: string; accent: string; parentId: number | null; kind: "root" | "sub" | "product";
};
const CATEGORY_ID_MAP = new Map<number, CatEntry>();
CATEGORY_SOURCES.forEach(v => {
  const register = (item: any, parentId: number | null, kind: CatEntry["kind"]) => {
    CATEGORY_ID_MAP.set(item.id, {
      id: item.id, name: item.name, icon: item.icon, image: item.image, color: item.color,
      vertical: v.label, accent: v.accent, parentId, kind,
    });
  };
  v.roots.forEach((c: any) => register(c, null, "root"));
  v.subs.forEach((s: any) => register(s, s.parentId ?? null, "sub"));
  v.products.forEach((p: any) => register(p, p.parentId ?? null, "product"));
});

/* walk any id up to its ultimate root ancestor — depth-agnostic, cycle-safe */
function getTopAncestor(id: number): CatEntry | undefined {
  let current = CATEGORY_ID_MAP.get(id);
  const seen = new Set<number>();
  while (current?.parentId != null && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = CATEGORY_ID_MAP.get(current.parentId);
    if (!parent) break;
    current = parent;
  }
  return current;
}

/* Firestore/back-end data is inconsistent about whether category ids
   come back as numbers or numeric strings. Every lookup against
   CATEGORY_ID_MAP (a Map<number, …>) needs a real number or it silently
   misses — normalize once here instead of at every call site. This was
   the root cause of the category chip row coming back empty: ids were
   arriving as strings and never matching the numeric keys the category
   files are registered under. */
const toId = (v: any): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/* attribute lookups (color / size swatches) for the product detail card */
const colorPalette = productColors[0]?.values ?? [];
const sizePalette   = [...Sizes, ...ShoeSizes];

function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.30) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* Gifts ships its own icon-resolution function instead of a plain icon
   field/component (same pattern Farm/Grocery use elsewhere in the app) —
   resolve it explicitly so gift category chips never render blank. */
const GIFTS_ITEM_IDS = new Set([...giftsCategories, ...giftsSubcategories, ...giftsProducts].map((i: any) => i.id));
type DomainIconRenderer = (item: any, size: number) => React.ReactNode;
const asDomainIconRenderer = (fn: unknown): DomainIconRenderer | undefined =>
  typeof fn === "function" ? (fn as unknown as DomainIconRenderer) : undefined;
const renderGiftsIconFn = asDomainIconRenderer(renderGiftsIcon);

/* recognise forwardRef/memo icon components too, not just plain functions,
   so an unusual icon shape never crashes the render */
const REACT_COMPONENT_TYPEOFS = new Set([Symbol.for("react.forward_ref"), Symbol.for("react.memo")]);
const isRenderableComponent = (icon: unknown): icon is React.ComponentType<{ size?: number; color?: string }> => {
  if (typeof icon === "function") return true;
  if (typeof icon === "object" && icon !== null && "$$typeof" in (icon as any)) {
    return REACT_COMPONENT_TYPEOFS.has((icon as any).$$typeof);
  }
  return false;
};

function renderCatIcon(item: any, size = 18, fallbackColor?: string) {
  if (!item) return null;
  const raw = item.icon;
  if (React.isValidElement(raw)) return raw;
  if (isRenderableComponent(raw)) {
    const Icon = raw as React.ComponentType<{ size?: number; color?: string }>;
    return <Icon size={size} color={fallbackColor ?? item.color ?? F.ink} />;
  }
  return null;
}

/* the one function every category icon in this file should go through —
   tries the gifts-specific renderer first, then the generic one, and
   never throws even if a category's icon shape is unexpected */
function resolveItemIcon(item: any, size: number, color?: string): React.ReactNode {
  if (!item) return null;
  try {
    if (item.id != null && GIFTS_ITEM_IDS.has(item.id) && renderGiftsIconFn) {
      const rendered = renderGiftsIconFn(item, size);
      if (rendered) return <span style={{ display: "flex", alignItems: "center", color }}>{rendered}</span>;
    }
  } catch {
    // domain renderer threw — fall through to the generic one below
  }
  return renderCatIcon(item, size, color);
}

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800;900&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }

@keyframes gradSpin {
  0%   { background-position: 0%   50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0%   50%; }
}

input::placeholder { color: rgba(255,255,255,.55); }

.sh-card { transition: box-shadow .2s, transform .2s; }
.sh-card:hover { box-shadow: 0 14px 36px ${RGBA_ORANGE(0.18)}; transform: translateY(-4px); }

.sh-plus { transition: transform .15s; }
.sh-plus:hover  { transform: scale(1.08); }
.sh-plus:active { transform: scale(.92); }

.sh-cat {
  flex-shrink: 0; border: none; cursor: pointer; border-radius: 999px;
  font-weight: 800; letter-spacing: .02em; transition: transform .15s ease, box-shadow .15s ease;
}
.sh-cat:hover { transform: translateY(-2px); }

.sh-backdrop { position: fixed; inset: 0; background: ${RGBA_DARK(0.5)}; z-index: 1000; }

.sh-add-btn { transition: transform .18s ease, box-shadow .18s ease; }
.sh-add-btn:hover  { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.sh-add-btn:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.tab-item {
  display: flex; flex-direction: column; align-items: center;
  gap: 3px; font-size: 10px; font-weight: 700; cursor: pointer;
  color: ${F.mutedDim}; padding: 4px 10px; transition: color .2s;
}
.tab-item.active { color: ${F.orange}; }

.sh-nav-item { display:flex; align-items:center; gap:10px; padding:11px 4px; cursor:pointer; font-size:13px; font-weight:700; color:${F.ink}; background:none; width:100%; text-align:left; border:none; }

.page-watermark{
  position:fixed;top:50%;left:50%;
  width:min(70vw,640px);height:min(70vw,640px);
  transform:translate(-50%,-50%);
  opacity:.035;pointer-events:none;z-index:0;
  object-fit:contain;
}
`;

/* ═══════════════════════════════════════════════════════════
   LAYOUT STYLES
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page:       { minHeight:"100vh", background:F.pageBg, paddingBottom:90, fontFamily:"'Montserrat',sans-serif", position:"relative", overflow:"hidden" },
  headerTop:  { background:F.headerBg, backgroundImage:F.grad, position:"sticky", top:0, zIndex:200, boxShadow:"0 4px 24px rgba(255,61,87,.25)" },
  headerRow:  { display:"flex", alignItems:"center", gap:10, padding:"12px 16px 10px" },
  searchWrap: { display:"flex", alignItems:"center", flex:1, background:"rgba(255,255,255,.18)", borderRadius:999, overflow:"hidden", border:"1.5px solid rgba(255,255,255,.32)" },
  searchInput:{ flex:1, border:"none", outline:"none", padding:"10px 16px", fontSize:14, color:"#fff", background:"transparent", fontFamily:"inherit" },
  searchBtn:  { width:44, minHeight:42, background:"rgba(255,255,255,.22)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, borderRadius:"0 999px 999px 0" },
  hBtn:       { display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,.16)", border:"1px solid rgba(255,255,255,.28)", cursor:"pointer", color:"#fff", fontSize:12, fontWeight:700, padding:"7px 13px", borderRadius:999, flexShrink:0, whiteSpace:"nowrap" as const },
  grid:       { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:18 },
  mobileGrid: { display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12 },
  mobileTabs: { position:"fixed" as const, bottom:0, left:0, right:0, height:64, background:"#fff", display:"flex", justifyContent:"space-around", alignItems:"center", boxShadow:"0 -4px 24px rgba(255,122,26,.12)", zIndex:100, borderTop:`1px solid ${F.border}` },
  desktopGrid:{ display:"grid", gap:24 },
  sidebar:    { background:"#fff", borderRadius:20, padding:18, boxShadow:"0 4px 24px rgba(43,26,18,.08)", position:"sticky" as const, top:96, maxHeight:"calc(100vh - 116px)", overflowY:"auto" as const, border:`1px solid ${F.border}` },
  commerceCard:   { borderRadius:16, padding:14, background:F.offwhite, marginBottom:12, border:`1px solid ${F.border}` },
  commerceHeader: { fontSize:14, fontWeight:900, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", color:F.ink },
  category:   { padding:9, borderRadius:12, background:"#fff", marginTop:8, border:`1px solid ${F.border}`, cursor:"pointer" },
  row:        { display:"flex", alignItems:"center", gap:9, fontWeight:700, color:F.ink, fontSize:13 },
  sub:        { paddingLeft:20, paddingTop:8, display:"flex", flexDirection:"column" as const, gap:6 },
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const StoreHome: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const dispatch    = useDispatch<AppDispatch>();
  const navigate    = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const userId     = authUser?.userId;

  const notifications = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount   = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);

  const partner    = useSelector((s: RootState) => s.commercePartners.currentPartner);
  const brandProducts = useSelector((s: RootState) => s.products.brandProducts);
  const brandLoading  = useSelector((s: RootState) => s.products.brandProductsLoading);
  const cartItems  = useSelector((s: RootState) => s.cart.items);
  const cartTotal  = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const ratingSummaries = useSelector((s: RootState) => s.productRatings.summaries);

  const [isMobile,      setIsMobile]      = useState(window.innerWidth <= 768);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const [activeCat, setActiveCat] = useState<number | null>(null);
  const [activeSub, setActiveSub] = useState<number | null>(null);

  const [catOpen,        setCatOpen]        = useState(false); // mobile bottom sheet
  const [desktopCatOpen, setDesktopCatOpen] = useState(false); // desktop sidebar
  const [openVertical,   setOpenVertical]   = useState<string | null>(null);
  const [openSub,        setOpenSub]        = useState<number | null>(null);

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [imgIndex,     setImgIndex]     = useState(0);
  const [detailQty,    setDetailQty]    = useState(1);
  const [detailColor,  setDetailColor]  = useState<number | null>(null);
  const [detailSize,   setDetailSize]   = useState<number | null>(null);

  /* responsive */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* auth guard */
  useEffect(() => {
    if (!authUser && !userId) navigate("/AuthSwitch");
  }, [authUser, userId, navigate]);

  /* brand data */
  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToCommerceData(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  /* brand products — scoped listener */
  useEffect(() => {
    if (!brandId) return;
    dispatch(listenToBrandProductsFeed(brandId) as any);
    dispatch(fetchAllProductSummaries());
    return () => { dispatch(stopBrandProductsFeed() as any); };
  }, [brandId, dispatch]);

  /* notifications */
  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  /* ═══════════════════════════════════════════════════════════
     CATEGORY DISCOVERY — walks every id a *published* listing
     carries (categoryId / subcategoryId / productId) up to its
     top-level root via getTopAncestor, so the chip row always
     reflects what this store actually has live — regardless of
     which field a listing's tag ended up on, and at any depth.
     Anything outside our master category files (custom/legacy
     brand categories) still counts, keyed by its own raw
     categoryId, so nothing silently disappears from the row.

     Two defensive fixes vs. the previous version, both of which
     could silently empty this whole row out:
       1. ids are normalized through toId() before every Map/Set
          lookup — categoryId/subcategoryId/productId sometimes
          arrive as numeric strings, which never match the
          Map<number,…> keys the category files are registered under.
       2. "published" only excludes listings EXPLICITLY marked
          isPublished === false — a listing with the field simply
          missing is treated as published, same as everywhere else
          products already show up for this brand.
  ═══════════════════════════════════════════════════════════ */
  const { rootIds, subIdsFull, productCountByRoot } = useMemo(() => {
    const roots = new Set<number>();
    const subs  = new Set<number>();
    const countByRoot = new Map<number, number>();

    brandProducts.forEach((l: any) => {
      if (l.isPublished === false) return;
      const tagIds = [toId(l.categoryId), toId(l.subcategoryId), toId(l.productId)].filter((v): v is number => v != null);
      if (!tagIds.length) return;

      let root: number | null = null;
      tagIds.forEach(id => {
        const entry = CATEGORY_ID_MAP.get(id);
        if (entry?.kind === "sub") subs.add(id);
        if (entry?.kind === "product" && entry.parentId != null) subs.add(entry.parentId);
        const top = getTopAncestor(id);
        if (top) { roots.add(top.id); root = root ?? top.id; }
      });

      if (root == null) {
        const fallbackCat = toId(l.categoryId);
        if (fallbackCat != null) { roots.add(fallbackCat); root = fallbackCat; }
      }
      if (root != null) countByRoot.set(root, (countByRoot.get(root) ?? 0) + 1);
    });

    return { rootIds: roots, subIdsFull: subs, productCountByRoot: countByRoot };
  }, [brandProducts]);

  /* chips row — icon + name + a cute little product-count badge,
     only categories this store actually has live products in */
  const storeCategories = useMemo(() => {
    const list: any[] = [];
    const known = new Set<number>();
    CATEGORY_SOURCES.forEach(v => {
      v.roots.forEach((c: any) => {
        if (rootIds.has(c.id)) {
          list.push({ ...c, accent: v.accent, verticalLabel: v.label, count: productCountByRoot.get(c.id) ?? 0 });
          known.add(c.id);
        }
      });
    });
    brandProducts.forEach((l: any) => {
      const catId = toId(l.categoryId);
      if (catId != null && rootIds.has(catId) && !known.has(catId)) {
        list.push({ id: catId, name: l.categoryName ?? `Category ${catId}`, accent: F.orange, verticalLabel: null, count: productCountByRoot.get(catId) ?? 0 });
        known.add(catId);
      }
    });
    return list.sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [rootIds, brandProducts, productCountByRoot]);

  /* verticals present in this store — used to build the sidebar / sheet tree */
  const usedVerticals = useMemo(() => {
    return CATEGORY_SOURCES.filter(v => v.roots.some((c: any) => rootIds.has(c.id)));
  }, [rootIds]);

  /* cart helpers */
  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const current = cartItems[0]?.brandId;
    if (current && newBrandId && current !== newBrandId) dispatch(clearCart());
  };

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };
  const handleRemoveFromCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch(decrementCartItem({ listingId: item.listingId }));
  };

  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/productcheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems },
    });
  };

  const handleCatSelect = (catId: number) => {
    setActiveCat(prev => (prev === catId ? null : catId));
    setActiveSub(null);
  };
  const handleSubSelect = (subId: number) => setActiveSub(subId);

  const resetFilters = () => { setActiveCat(null); setActiveSub(null); setSearchQuery(""); };
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  /* filtered products — same toId() normalization as the category
     discovery above, so filtering by a chip never mismatches string
     vs number ids either */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return brandProducts.filter((l: any) => {
      if (l.isPublished === false) return false;
      const catId  = toId(l.categoryId);
      const subId  = toId(l.subcategoryId);
      const prodId = toId(l.productId);
      if (activeCat != null) {
        const matchesCat = catId === activeCat || subId === activeCat || prodId === activeCat;
        if (!matchesCat) return false;
      }
      if (activeSub != null) {
        const matchesSub = subId === activeSub || prodId === activeSub;
        if (!matchesSub) return false;
      }
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [brandProducts, searchQuery, activeCat, activeSub]);

  const activeCatName = activeCat != null ? (CATEGORY_ID_MAP.get(activeCat)?.name ?? storeCategories.find(c => c.id === activeCat)?.name ?? null) : null;
  const activeSubName = activeSub != null ? (CATEGORY_ID_MAP.get(activeSub)?.name ?? null) : null;

  /* brand info */
  const shopName = partner?.name  || "Store";
  const shopLogo = partner?.image || logo;
  const shopCity = partner?.address?.city    || null;
  const shopCountry = partner?.address?.country || null;

  /* ── open / close the product detail sheet-or-modal ── */
  const openDetails = (item: any) => {
    setImgIndex(0);
    setDetailQty(1);
    setDetailColor(null);
    setDetailSize(null);
    setSelectedItem(item);
  };
  const closeDetails = () => setSelectedItem(null);

  /* lock background scroll while the detail view is open */
  useEffect(() => {
    if (!selectedItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [selectedItem]);

  /* ─── HEADER ─── */
  const Header = () => (
    <div style={S.headerRow}>
      <button onClick={() => navigate(-1)} style={{ ...S.hBtn, padding:"7px 10px" }}>
        <ArrowLeft size={16} color="#fff" />
      </button>

      <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
        <div style={{ position:"relative", display:"inline-flex" }}>
          <div style={{ background:"#fff", borderRadius:"50%", padding:2, display:"inline-flex" }}>
            <img src={shopLogo} alt={shopName}
              style={{ width:32, height:32, borderRadius:"50%", objectFit:"cover", display:"block" }}
              onError={e => { (e.currentTarget as HTMLImageElement).src = logo; }} />
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", lineHeight:1.2 }}>
          <span style={{ fontFamily:"'Baloo 2',sans-serif", fontSize:15, fontWeight:800, color:"#fff" }}>
            {shopName}
          </span>
          {(shopCity || shopCountry) && (
            <span style={{ fontSize:10, color:"rgba(255,255,255,.85)", display:"flex", alignItems:"center", gap:3 }}>
              <MapPin size={9} color="rgba(255,255,255,.85)" />
              {[shopCity, shopCountry].filter(Boolean).join(", ")}
            </span>
          )}
        </div>
      </div>

      <div style={{ ...S.searchWrap, borderColor: searchFocused ? "#fff" : "rgba(255,255,255,.32)", boxShadow: searchFocused ? "0 0 0 3px rgba(255,255,255,.18)" : "none" }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
          placeholder={`Search ${shopName}…`} style={S.searchInput} />
        {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background:"none", border:"none", cursor:"pointer", padding:"0 8px", color:"#fff" }}><X size={14} /></button>}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      {storeCategories.length > 0 && (
        <button
          onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v))}
          style={{ ...S.hBtn, background: desktopCatOpen ? "rgba(255,255,255,.34)" : (S.hBtn.background as string) }}>
          <LayoutGrid size={16} color="#fff" />
          {!isMobile && <span>Categories</span>}
        </button>
      )}

      <div style={{ position:"relative", cursor:"pointer", flexShrink:0 }} onClick={() => navigate(`/notifications/${userId}`)}>
        <Notifications style={{ fontSize:24, color:"#fff" }} />
        {unreadCount > 0 && (
          <span style={{ position:"absolute", top:-5, right:-5, background:"#fff", color:F.red, fontSize:9, fontWeight:900, width:17, height:17, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{unreadCount}</span>
        )}
      </div>

      <button onClick={goToCheckout} style={{ ...S.hBtn, position:"relative", background: cartTotal > 0 ? "rgba(255,255,255,.32)" : "rgba(255,255,255,.16)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && <span style={{ position:"absolute", top:-4, right:-4, background:"#fff", color:F.red, fontSize:9, fontWeight:900, width:17, height:17, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
      </button>

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/ClientDashboard/${userId}`)} style={S.hBtn}><ShoppingBag size={16} /><span>Orders</span></button>
          <button onClick={() => navigate(`/profile/${userId}`)} style={S.hBtn}><User size={16} /></button>
        </>
      )}
    </div>
  );

  /* ─── CATEGORY ICON CHIP ROW — icon + name + a cute count badge,
       this brand's own categories only ─── */
  const CategoryChips = () => {
    if (!storeCategories.length) return null;
    return (
      <div style={{ display:"flex", gap:14, overflowX:"auto", padding: isMobile ? "0 12px 14px" : "0 24px 18px" }}>
        <motion.button whileHover={{ y:-3 }} whileTap={{ scale:0.9 }}
          onClick={() => { setActiveCat(null); setActiveSub(null); }}
          style={{ background:"none", border:"none", cursor:"pointer", flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
          <div style={{
            width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius:"50%",
            background: activeCat === null ? F.gradH : "#fff",
            border: `2px solid ${activeCat === null ? F.red : F.border}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow: activeCat === null ? "0 8px 20px rgba(255,61,87,.30)" : "0 2px 8px rgba(43,26,18,.06)",
          }}>
            <ShoppingBasket size={isMobile ? 20 : 24} color={activeCat === null ? "#fff" : F.orange} />
          </div>
          <span style={{ fontSize:10, fontWeight: activeCat === null ? 800 : 600, color: activeCat === null ? F.red : F.ink }}>All</span>
        </motion.button>

        {storeCategories.map(c => {
          const isSel = activeCat === c.id;
          const color = c.color ?? c.accent ?? F.orange;
          return (
            <motion.button key={c.id} whileHover={{ y:-3 }} whileTap={{ scale:0.9 }} onClick={() => handleCatSelect(c.id)}
              style={{ background:"none", border:"none", cursor:"pointer", flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", gap:6, position:"relative" }}>
              <div style={{
                width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius:"50%", overflow:"hidden", position:"relative",
                border: `2px solid ${isSel ? F.red : F.border}`,
                boxShadow: isSel ? "0 0 0 3px rgba(255,61,87,.18)" : "none", transition:"all .2s",
              }}>
                {c.image ? (
                  <img src={c.image} alt={c.name} style={{ width:"100%", height:"100%", objectFit:"cover", opacity:isSel?1:0.88 }} />
                ) : (
                  <div style={{ width:"100%", height:"100%", background: color, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ color:"#fff", display:"flex" }}>{resolveItemIcon(c, isMobile ? 20 : 24, "#fff") ?? <Package size={isMobile?20:24} color="#fff" />}</span>
                  </div>
                )}
              </div>
              {c.count > 1 && (
                <span style={{
                  position:"absolute", top:-2, right:-2, minWidth:16, height:16, padding:"0 4px", borderRadius:999,
                  background:F.red, color:"#fff", fontSize:9, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center",
                  border:"1.5px solid #fff", boxShadow:"0 1px 4px rgba(43,26,18,.25)",
                }}>
                  {c.count > 99 ? "99+" : c.count}
                </span>
              )}
              <span style={{ fontSize:10, fontWeight: isSel ? 800 : 600, color: isSel ? F.red : F.ink, whiteSpace:"nowrap", maxWidth:70, overflow:"hidden", textOverflow:"ellipsis" }}>{c.name}</span>
            </motion.button>
          );
        })}
      </div>
    );
  };

  /* ─── CATEGORY TREE (per vertical) — sidebar (desktop) + bottom sheet (mobile) ─── */
  const StoreCategoryTree: React.FC<{ v: CatSource }> = ({ v }) => {
    const isOpen = openVertical === v.slug;
    const usedRoots = v.roots.filter((c: any) => rootIds.has(c.id));
    if (!usedRoots.length) return null;

    return (
      <div style={S.commerceCard}>
        <div style={S.commerceHeader} onClick={() => setOpenVertical(isOpen ? null : v.slug)}>
          {v.label}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} color={F.muted} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }} exit={{ height:0, opacity:0 }} style={{ overflow:"hidden" }}>
              {usedRoots.map((cat: any) => {
                const isCatOpen = openSub === cat.id;
                const subsForCat = v.subs.filter((s: any) => s.parentId === cat.id && subIdsFull.has(s.id));
                return (
                  <div key={cat.id} style={S.category}>
                    <div style={S.row} onClick={() => { setOpenSub(isCatOpen ? null : cat.id); handleCatSelect(cat.id); }}>
                      {resolveItemIcon(cat, 15, v.accent)}
                      {cat.name}
                      <motion.div animate={{ rotate: isCatOpen ? 90 : 0 }} style={{ marginLeft:"auto" }}><ChevronRight size={13} color={F.muted} /></motion.div>
                    </div>
                    <AnimatePresence>
                      {isCatOpen && subsForCat.length > 0 && (
                        <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }} exit={{ height:0, opacity:0 }} style={{ overflow:"hidden" }}>
                          <div style={S.sub}>
                            {subsForCat.map((sub: any) => (
                              <div key={sub.id}
                                style={{ ...S.row, color: activeSub === sub.id ? F.red : F.ink, fontWeight: activeSub === sub.id ? 800 : 600 }}
                                onClick={() => { handleSubSelect(sub.id); closePanel(); }}>
                                {resolveItemIcon(sub, 13, v.accent)}
                                {sub.name}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      <button onClick={() => { resetFilters(); closePanel(); }}
        className="sh-nav-item" style={{ borderBottom:`1px solid ${F.border}`, marginBottom:8, color: activeCat === null ? F.red : F.ink }}>
        <ShoppingBasket size={16} color={activeCat === null ? F.red : F.muted} /> All Products
      </button>
      {usedVerticals.map(v => <StoreCategoryTree key={v.slug} v={v} />)}
      {usedVerticals.length === 0 && (
        <div style={{ fontSize:12, color:F.muted, padding:"12px 4px" }}>No categories yet.</div>
      )}
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setCatOpen(false)} className="sh-backdrop" />
          <motion.div initial={{ y:"100%" }} animate={{ y:0 }} exit={{ y:"100%" }} transition={{ type:"spring", stiffness:340, damping:30 }}
            style={{ position:"fixed", bottom:0, left:0, right:0, maxHeight:"82vh", borderRadius:"22px 22px 0 0", background:"#fff", zIndex:1001, overflowY:"auto", boxShadow:`0 -8px 40px ${RGBA_DARK(0.25)}` }}>
            <div style={{ width:36, height:4, background:F.border, borderRadius:2, margin:"14px auto 0" }} />
            <div style={{ display:"flex", alignItems:"center", borderBottom:`1px solid ${F.border}`, padding:"12px 20px 10px" }}>
              <span style={{ fontWeight:800, fontSize:13, color:F.ink }}>Categories</span>
              <div style={{ flex:1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><X size={16} color={F.muted} /></button>
            </div>
            <div style={{ padding:"14px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     PRODUCT CARD
  ═══════════════════════════════════ */
  const ProductCard = ({ item }: { item: any }) => {
    const q       = getQty(item.listingId);
    const price   = `${symbol || "€"}${((item.rate ?? 0) * (rate ?? 1)).toFixed(2)}`;
    const summary = ratingSummaries[item.listingId];
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;

    return (
      <div className="sh-card" onClick={() => openDetails(item)}
        style={{ background:F.cardBg, border:`1.5px solid ${F.border}`, borderRadius:16, padding:14, cursor:"pointer", position:"relative", display:"flex", flexDirection:"column" }}>
        {discount && (
          <span style={{ position:"absolute", top:10, left:10, zIndex:2, background:F.red, color:"#fff", fontSize:10, fontWeight:800, padding:"3px 9px", borderRadius:6, letterSpacing:".02em" }}>
            {discount}% OFF
          </span>
        )}
        <div style={{ width:"100%", aspectRatio:"1/1", borderRadius:12, overflow:"hidden", background:F.cream, marginBottom:10, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={e => { e.stopPropagation(); openDetails(item); }}>
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}
                onError={e => { (e.currentTarget as HTMLImageElement).src = logo; (e.currentTarget as HTMLImageElement).style.objectFit = "contain"; (e.currentTarget as HTMLImageElement).style.opacity = "0.4"; }} />
            : <Package size={32} color={F.orange} style={{ opacity:.4 }} />}
        </div>

        <div style={{ fontSize:13.5, fontWeight:700, color:F.ink, lineHeight:1.3, marginBottom:2, fontFamily:"'Baloo 2',sans-serif", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" as any, overflow:"hidden", minHeight:34 }}>
          {item.name}
        </div>
        <div style={{ fontSize:11.5, color:F.muted, marginBottom:10 }}>
          {item.unit || "item"}
          {summary?.totalReviews ? ` · ${summary.avgRating.toFixed(1)}★ (${summary.totalReviews})` : ""}
        </div>

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:"auto" }}>
          <span style={{ fontSize:15, fontWeight:800, color:F.ink }}>{price}</span>
          {q > 0 ? (
            <div onClick={e => e.stopPropagation()} style={{ display:"flex", alignItems:"center", gap:6, background:F.cream, borderRadius:999, padding:"3px 6px" }}>
              <button className="sh-plus" onClick={e => handleRemoveFromCart(item, e)} style={{ width:22, height:22, borderRadius:"50%", border:"none", background:F.orange, color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Minus size={12} />
              </button>
              <span style={{ fontSize:12, fontWeight:800, color:F.ink, minWidth:12, textAlign:"center" }}>{q}</span>
              <button className="sh-plus" onClick={e => handleAddToCart(item, e)} style={{ width:22, height:22, borderRadius:"50%", border:"none", background:F.orange, color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Plus size={13} />
              </button>
            </div>
          ) : (
            <button className="sh-plus" onClick={e => handleAddToCart(item, e)}
              style={{ width:30, height:30, borderRadius:"50%", border:"none", background:F.green, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", boxShadow:"0 3px 10px rgba(44,94,52,.35)" }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     INLINE DETAIL VIEW — small image, full product details:
     category breadcrumb, description, color/size attributes
     (pulled from the shared attribute library) when present,
     plus a generic spec sheet for any other flat attribute
     fields a product happens to carry.
  ═══════════════════════════════════ */
  const DetailView = () => {
    if (!selectedItem) return null;
    const item      = selectedItem;
    const photos    = item.photos?.length ? item.photos : [];
    const summary   = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const qty       = getQty(item.listingId);
    const unitPrice = (item.rate ?? 0) * (rate ?? 1);
    const price     = `${symbol ?? "€"}${unitPrice.toFixed(2)}`;
    const linePrice = `${symbol ?? "€"}${(unitPrice * detailQty).toFixed(2)}`;

    const catEntry = item.categoryId != null ? CATEGORY_ID_MAP.get(toId(item.categoryId) ?? -1) : null;
    const subEntry = item.subcategoryId != null ? CATEGORY_ID_MAP.get(toId(item.subcategoryId) ?? -1) : null;
    const breadcrumb = [catEntry?.name ?? item.categoryName, subEntry?.name].filter(Boolean).join(" › ");

    /* colour ids can arrive two ways in this codebase: as the keys of
       a `variants` map (per-colour photo/stock overrides), or as a
       plain list under attributes[GENERAL_COLOR_ATTRIBUTE_ID] with no
       variants map at all. Reading only `variants` (the old behaviour)
       meant any product tagged with colours but no photo overrides
       showed no colour swatches whatsoever — merge both sources. */
    const colorIdsFromVariants: number[] = item.variants ? Object.keys(item.variants).map(Number) : [];
    const colorIdsFromAttrs: number[] = Array.isArray(item.attributes?.[GENERAL_COLOR_ATTRIBUTE_ID])
      ? item.attributes[GENERAL_COLOR_ATTRIBUTE_ID]
      : [];
    const variantColorIds: number[] = Array.from(new Set([...colorIdsFromVariants, ...colorIdsFromAttrs]));

    const sizeIdsForColor: number[] = detailColor && item.variants?.[detailColor]?.sizeIds ? item.variants[detailColor].sizeIds : [];
    const previewImg = detailColor && item.variants?.[detailColor]?.imageUrl ? item.variants[detailColor].imageUrl : photos[imgIndex];

    /* generic spec sheet — any plain (non-variant) descriptive fields
       a product happens to carry. Every row is optional; only fields
       that are actually present on the listing render, so this never
       shows empty/placeholder rows for products without them. */
    const specRows: { label: string; value: string }[] = [
      { label: "Brand",          value: item.brand },
      { label: "Material",       value: item.material },
      { label: "Model",          value: item.model },
      { label: "Weight",         value: item.weight ? `${item.weight}${item.weightUnit ?? ""}` : null },
      { label: "Dimensions",     value: item.dimensions },
      { label: "SKU",            value: item.sku },
      { label: "Country of origin", value: item.countryOfOrigin },
      { label: "Warranty",       value: item.warranty },
    ].filter((r): r is { label: string; value: string } => !!r.value);

    const addFromDetail = () => {
      ensureCartBrandCompat(item.brandId);
      for (let i = 0; i < detailQty; i++) {
        dispatch(addToCart({
          listingId: item.listingId, name: item.name!, rate: item.rate!,
          photo: previewImg ?? item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
          colorId: detailColor ?? undefined, sizeId: detailSize ?? undefined,
        } as any));
      }
      closeDetails();
    };

    const canAdd = variantColorIds.length === 0 || detailColor !== null;

    const Content = () => (
      <>
        {/* ── SMALL image zone ── */}
        <div style={{ position:"relative", width:"100%", height: isMobile ? 170 : 200, background:F.cream, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {previewImg ? (
            <img src={previewImg} alt={item.name} style={{ maxWidth:"58%", maxHeight:"82%", objectFit:"contain" }} />
          ) : (
            <Package size={40} color={F.orange} style={{ opacity:0.4 }} />
          )}
          {photos.length > 1 && !detailColor && (
            <>
              <button onClick={() => setImgIndex(i => (i - 1 + photos.length) % photos.length)}
                style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,.88)", border:"none", width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                <ChevronLeft size={14} color={F.ink} />
              </button>
              <button onClick={() => setImgIndex(i => (i + 1) % photos.length)}
                style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,.88)", border:"none", width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                <ChevronRight size={14} color={F.ink} />
              </button>
              <div style={{ position:"absolute", bottom:8, left:0, right:0, display:"flex", justifyContent:"center", gap:5 }}>
                {photos.map((_: string, i: number) => (
                  <div key={i} style={{ width: i === imgIndex ? 14 : 5, height:5, borderRadius:3, background: i === imgIndex ? F.orange : "rgba(255,255,255,.7)", transition:"all .2s" }} />
                ))}
              </div>
            </>
          )}
          <button onClick={closeDetails} style={{ position:"absolute", top:10, right:10, background:"rgba(255,255,255,.92)", border:"none", width:30, height:30, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", boxShadow:`0 2px 8px ${RGBA_DARK(0.18)}` }}>
            <X size={15} color={F.ink} />
          </button>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:"16px 20px 8px" }}>
          {shopName && (
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <img src={shopLogo} alt={shopName} style={{ width:20, height:20, borderRadius:"50%", objectFit:"cover" }} onError={e => { (e.currentTarget as HTMLImageElement).src = logo; }} />
              <span style={{ fontSize:11.5, fontWeight:700, color:F.muted }}>{shopName}</span>
            </div>
          )}

          {breadcrumb && (
            <div style={{ fontSize:10.5, fontWeight:700, letterSpacing:".04em", color:F.mutedDim, textTransform:"uppercase", marginBottom:6 }}>
              {breadcrumb}
            </div>
          )}

          <h2 style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:800, fontSize:18, color:F.ink, marginBottom:8, lineHeight:1.25 }}>
            {item.name}
          </h2>

          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <span style={{ display:"inline-flex", alignItems:"center", background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", borderRadius:999, padding:"4px 14px", fontWeight:900, color:"#fff", fontSize:15 }}>
              {price}
            </span>
            {hasRating && (
              <div style={{ display:"flex", alignItems:"center", gap:3, background:F.cream, borderRadius:999, padding:"4px 10px" }}>
                <Star size={12} fill={F.orange} color={F.orange} />
                <span style={{ fontSize:12, fontWeight:800, color:F.ink }}>{summary.avgRating.toFixed(1)}</span>
                <span style={{ fontSize:11, color:F.muted }}>({summary.totalReviews.toLocaleString()})</span>
              </div>
            )}
          </div>

          {item.description && (
            <p style={{ fontSize:13, color:F.muted, lineHeight:1.7, marginBottom:16 }}>{item.description}</p>
          )}

          {/* ── color attribute — merged from variants + attributes,
                see variantColorIds above ── */}
          {variantColorIds.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".08em", color:F.muted, marginBottom:8, textTransform:"uppercase" }}>
                Color
                {detailColor && (() => {
                  const cv = colorPalette.find((c: any) => c.id === detailColor);
                  return cv ? <span style={{ fontWeight:600, color:F.orange, textTransform:"none", letterSpacing:0 }}> — {cv.name}</span> : null;
                })()}
              </div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {variantColorIds.map(cId => {
                  const cv = colorPalette.find((c: any) => c.id === cId);
                  const hex = (cv as any)?.hexCodes?.[0];
                  const isSel = detailColor === cId;
                  return (
                    <button key={cId} onClick={() => { setDetailColor(cId); setDetailSize(null); }}
                      style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                      <div style={{
                        width:32, height:32, borderRadius:"50%",
                        background: hex ? sphere3D(hex) : sphere3D(F.ink),
                        outline: isSel ? `2.5px solid ${F.red}` : `1px solid ${F.border}`, outlineOffset:2,
                      }} />
                      <span style={{ fontSize:9, fontWeight: isSel ? 700 : 500, color: isSel ? F.red : F.mutedDim }}>{cv?.name ?? String(cId)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {detailColor && sizeIdsForColor.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".08em", color:F.muted, marginBottom:8, textTransform:"uppercase" }}>Size</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {sizeIdsForColor.map((sId: number) => {
                  const sz = sizePalette.find((s: any) => s.id === sId);
                  const isSel = detailSize === sId;
                  return (
                    <button key={sId} onClick={() => setDetailSize(sId)}
                      style={{ padding:"7px 14px", border:`1.5px solid ${isSel ? F.ink : F.border}`, background: isSel ? F.ink : "#fff", color: isSel ? "#fff" : F.ink, fontSize:11, fontWeight:700, cursor:"pointer", borderRadius:8 }}>
                      {sz?.name ?? sId}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── generic spec sheet — brand/material/weight/etc, only
                the fields this particular product actually has ── */}
          {specRows.length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:16, padding:"12px 14px", background:F.offwhite, borderRadius:12 }}>
              {specRows.map(row => (
                <div key={row.label} style={{ display:"flex", alignItems:"baseline", gap:8, fontSize:12, color:F.ink }}>
                  <span style={{ fontWeight:800, color:F.muted, minWidth:96, flexShrink:0 }}>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── quantity stepper for this add ── */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:".08em", color:F.muted, marginBottom:8, textTransform:"uppercase" }}>
              Quantity
            </div>
            <div style={{ display:"inline-flex", alignItems:"center", border:`1.5px solid ${F.border}`, borderRadius:12, overflow:"hidden", background:F.offwhite }}>
              <button onClick={() => setDetailQty(v => Math.max(1, v - 1))} style={{ width:38, height:38, border:"none", background:"transparent", cursor:"pointer", fontSize:15, color:F.red }}>−</button>
              <div style={{ width:40, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:F.ink, borderLeft:`1px solid ${F.border}`, borderRight:`1px solid ${F.border}` }}>{detailQty}</div>
              <button onClick={() => setDetailQty(v => v + 1)} style={{ width:38, height:38, border:"none", background:"transparent", cursor:"pointer", fontSize:15, color:F.red }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding:"12px 20px 18px", borderTop:`1px solid ${F.border}`, background:"#fff", flexShrink:0 }}>
          {qty > 0 && variantColorIds.length === 0 ? (
            <div className="sh-add-btn" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", height:50, background:"#fff", border:`2px solid ${F.orange}`, borderRadius:999, padding:"0 8px" }}>
              <button onClick={() => handleRemoveFromCart(item)} style={{ width:36, height:36, borderRadius:"50%", border:"none", background:F.orange, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                <Minus size={16} color="#fff" />
              </button>
              <span style={{ color:F.orange, fontWeight:900, fontSize:17 }}>{qty} in cart</span>
              <button onClick={() => handleAddToCart(item)} style={{ width:36, height:36, borderRadius:"50%", border:"none", background:F.orange, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                <Plus size={16} color="#fff" />
              </button>
            </div>
          ) : (
            <button className="sh-add-btn" disabled={!canAdd} onClick={addFromDetail}
              style={{ width:"100%", height:50, borderRadius:999, border:"none", background: canAdd ? F.grad : "#D9D0C6", display:"flex", alignItems:"center", justifyContent:"center", gap:8, cursor: canAdd ? "pointer" : "not-allowed", boxShadow: canAdd ? `0 6px 20px ${RGBA_RED(0.3)}` : "none" }}>
              <ShoppingCart size={17} color="#fff" />
              <span style={{ color:"#fff", fontWeight:800, fontSize:14, letterSpacing:"0.04em" }}>
                {canAdd ? `ADD TO BAG · ${linePrice}` : "SELECT A COLOR"}
              </span>
            </button>
          )}
        </div>
      </>
    );

    return createPortal(
      <AnimatePresence>
        <motion.div key="sh-backdrop" className="sh-backdrop" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} onClick={closeDetails} />
        {isMobile ? (
          <motion.div key="sh-sheet" initial={{ y:"100%" }} animate={{ y:0 }} exit={{ y:"100%" }} transition={{ type:"spring", stiffness:340, damping:32 }}
            style={{ position:"fixed", left:0, right:0, bottom:0, maxHeight:"88vh", background:"#fff", borderRadius:"22px 22px 0 0", zIndex:1001, display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:`0 -8px 40px ${RGBA_DARK(0.25)}` }}>
            <div style={{ width:36, height:4, borderRadius:2, background:RGBA_ORANGE(0.3), margin:"10px auto 0", flexShrink:0 }} />
            <Content />
          </motion.div>
        ) : (
          <motion.div key="sh-modal-wrap" style={{ position:"fixed", inset:0, zIndex:1001, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={closeDetails}>
            <motion.div key="sh-modal" initial={{ opacity:0, y:24, scale:0.96 }} animate={{ opacity:1, y:0, scale:1 }} exit={{ opacity:0, y:24, scale:0.96 }}
              transition={{ type:"spring", stiffness:340, damping:32 }} onClick={e => e.stopPropagation()}
              style={{ width:420, maxWidth:"100%", maxHeight:"86vh", background:"#fff", borderRadius:22, display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:`0 30px 80px ${RGBA_DARK(0.32)}` }}>
              <Content />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  /* ─── PRODUCT GRID ─── */
  const renderProducts = () => {
    if (brandLoading) return (
      <div style={{ textAlign:"center", padding:56, position:"relative", zIndex:1 }}>
        <div style={{ width:36, height:36, borderRadius:"50%", border:`3px solid rgba(255,122,26,.2)`, borderTopColor:F.orange, animation:"gradSpin .8s linear infinite", margin:"0 auto 14px" }} />
        <p style={{ color:F.muted, fontSize:13 }}>Loading products…</p>
      </div>
    );

    if (!filteredProducts.length) return (
      <div style={{ textAlign:"center", padding:56, position:"relative", zIndex:1 }}>
        <div style={{ fontSize:52, marginBottom:14 }}>🛍️</div>
        <h3 style={{ fontWeight:900, color:F.ink, marginBottom:8, fontFamily:"'Baloo 2',sans-serif" }}>
          {searchQuery ? "No results found" : "No products yet"}
        </h3>
        <p style={{ color:F.muted, marginBottom:24, fontSize:13 }}>
          {searchQuery ? `No products match "${searchQuery}"` : "This store hasn't listed any products yet."}
        </p>
        {(searchQuery || activeCat != null || activeSub != null) && (
          <button onClick={resetFilters}
            style={{ padding:"12px 28px", borderRadius:999, border:"none", background:GRAD_H, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:13 }}>
            Clear Filters
          </button>
        )}
      </div>
    );

    return (
      <>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, position:"relative", zIndex:1, flexWrap:"wrap" }}>
          <div style={{ width:4, height:22, borderRadius:2, background:GRAD_H }} />
          <span style={{ fontWeight:900, fontSize:15, color:F.ink, fontFamily:"'Baloo 2',sans-serif" }}>
            {activeSubName ?? activeCatName ?? "All Products"}
          </span>
          <span style={{ fontSize:12, color:F.muted, background:F.offwhite, padding:"2px 9px", borderRadius:999 }}>{filteredProducts.length}</span>
        </div>

        <div style={{ ...(isMobile ? S.mobileGrid : S.grid), position:"relative", zIndex:1 }}>
          {filteredProducts.map((item: any) => (
            <ProductCard key={item.listingId} item={item} />
          ))}
        </div>
      </>
    );
  };

  /* ─── BOTTOM TABS (mobile) ─── */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon:<Home size={22} />,          label:"Home",   active:false, action:() => navigate(`/ProductsHome/${userId}`) },
        { icon:<ShoppingBasket size={22} />, label:"Stores", active:true,  action:() => navigate("/MetaStores") },
        { label:"Cart", active:false, icon:(
            <div style={{ position:"relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position:"absolute", top:-5, right:-5, background:F.red, color:"#fff", fontSize:8, fontWeight:900, width:16, height:16, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{cartItems.reduce((s: number, i: any) => s + i.qty, 0)}</span>}
            </div>
          ), action:goToCheckout },
        { icon:<ShoppingBag size={22} />, label:"Orders",  active:false, action:() => navigate(`/ClientDashboard/${userId}`) },
        { icon:<User size={22} />,         label:"Profile", active:false, action:() => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale:0.88 } as any} onClick={t.action}
          className={`tab-item${t.active ? " active" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.active ? F.orange : F.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ─── RENDER ─── */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>

      <img src={logo} alt="" className="page-watermark" />

      <div style={S.headerTop}>
        <Header />
      </div>

      <div style={{ paddingTop: isMobile ? 14 : 20 }}>
        <CategoryChips />
      </div>

      <MobileCategorySheet />

      {!isMobile ? (
        <div style={{ ...S.desktopGrid, padding:"0 24px", gridTemplateColumns: desktopCatOpen ? "280px 1fr" : "1fr" }}>
          {desktopCatOpen && (
            <aside style={S.sidebar}>
              <CategoryPanelContent />
            </aside>
          )}
          <main style={{ minWidth:0, position:"relative", zIndex:1 }}>
            {renderProducts()}
          </main>
        </div>
      ) : (
        <div style={{ padding:"0 12px", position:"relative", zIndex:1 }}>
          {renderProducts()}
        </div>
      )}

      <DetailView />
      <BottomTabs />
    </div>
  );
};

export default StoreHome;